import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Mail, CheckCircle2, AlertTriangle, Send, Unplug } from 'lucide-react';
import { API } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { inputCls, formatErr } from '../billing/shared';
import { DailyEmailsCard } from './DailyEmailsCard';

const LABEL = 'block text-xs uppercase tracking-wider text-slate-500 mb-1';
const PROVIDER_NAMES = { gmail: 'Gmail', outlook: 'Outlook / Office 365', zoho: 'Zoho Mail', custom: 'Other (SMTP)' };
const HINTS = {
  gmail: 'Use a Gmail App Password: Google Account → Security → 2-Step Verification → App passwords.',
  outlook: 'Use your Outlook password, or an app password if 2-step sign-in is on.',
  zoho: 'Use your Zoho password or an app-specific password. For zoho.com accounts set the server to smtp.zoho.com.',
  custom: 'Ask your email or hosting provider for the SMTP server, port and security type.',
};

export const useEmailSettings = () => {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const load = () => axios.get(`${API}/settings/email`, { headers: authHeader() }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line
  return [data, setData, load];
};

export const EmailWarning = ({ onOpen }) => {
  const [data] = useEmailSettings();
  if (!data || data.connected) return null;
  return (
    <button type="button" onClick={onOpen} className="w-full text-left flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 hover:border-red-300" data-testid="email-not-connected-warning">
      <AlertTriangle size={16} className="mt-0.5 shrink-0" />
      <span><b>Connect your email.</b> Invoices, reminders, daily emails and appointment confirmations are not being sent
        {data.skipped_count ? ` (${data.skipped_count} held back so far)` : ''}. Tap to set it up.</span>
    </button>
  );
};

const Status = ({ data }) => (data.connected ? (
  <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800" data-testid="email-status-connected">
    <CheckCircle2 size={15} /> Sending as <b>{data.sending_as}</b>
  </div>
) : (
  <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800" data-testid="email-status-disconnected">
    <AlertTriangle size={15} /> Not connected — no emails are being sent.
  </div>
));

const AccountForm = ({ data, onSaved }) => {
  const { authHeader } = useAuth();
  const a = data.account;
  const [v, setV] = useState({ provider: a.provider, host: a.host, port: a.port || '', security: a.security, username: a.username, password: '', from_name: a.from_name, reply_to: a.reply_to });
  const [busy, setBusy] = useState(''); const [err, setErr] = useState(''); const [ok, setOk] = useState('');
  const [testTo, setTestTo] = useState('');
  const set = (k, val) => setV((x) => ({ ...x, [k]: val }));
  const preset = data.providers[v.provider] || {};
  const call = async (key, fn, done) => {
    setBusy(key); setErr(''); setOk('');
    try { const r = await fn(); done(r.data); } catch (e) { setErr(formatErr(e)); } finally { setBusy(''); }
  };
  const save = () => call('save', () => axios.put(`${API}/settings/email/account`, { ...v, port: v.port ? Number(v.port) : null }, { headers: authHeader() }),
    (d) => { onSaved(d); set('password', ''); setOk('Email connected and checked.'); });
  const test = () => call('test', () => axios.post(`${API}/settings/email/test`, { to: testTo }, { headers: authHeader() }), (d) => setOk(`Test email sent to ${d.sent_to}.`));
  const disconnect = () => window.confirm('Stop sending emails from this account?') && call('off', () => axios.delete(`${API}/settings/email/account`, { headers: authHeader() }), onSaved);
  const custom = v.provider === 'custom' || v.provider === 'zoho';
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {Object.keys(data.providers).map((p) => (
          <button key={p} type="button" onClick={() => setV((x) => ({ ...x, provider: p, host: '', port: '', security: '' }))} className={`text-xs font-semibold rounded-full border px-3 py-1.5 transition-colors ${v.provider === p ? 'border-teal-600 bg-teal-600 text-white' : 'border-slate-300 text-slate-600 hover:border-teal-500'}`} data-testid={`email-provider-${p}`}>{PROVIDER_NAMES[p]}</button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className={LABEL}>Email address</label><input value={v.username} onChange={(e) => set('username', e.target.value)} placeholder="you@yourworkshop.com" className={inputCls} data-testid="email-username" /></div>
        <div><label className={LABEL}>{v.provider === 'gmail' ? 'App password' : 'Password'}</label><input type="password" value={v.password} onChange={(e) => set('password', e.target.value)} placeholder={a.password_set ? 'Saved — leave blank to keep' : ''} className={inputCls} data-testid="email-password" /></div>
        <div><label className={LABEL}>Sender name</label><input value={v.from_name} onChange={(e) => set('from_name', e.target.value)} placeholder="Your workshop name" className={inputCls} data-testid="email-from-name" /></div>
        <div><label className={LABEL}>Replies go to (optional)</label><input value={v.reply_to} onChange={(e) => set('reply_to', e.target.value)} placeholder="Same as email address" className={inputCls} data-testid="email-reply-to" /></div>
        {custom && <>
          <div><label className={LABEL}>SMTP server</label><input value={v.host} onChange={(e) => set('host', e.target.value)} placeholder={preset.host || 'smtp.yourprovider.com'} className={inputCls} data-testid="email-host" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className={LABEL}>Port</label><input type="number" value={v.port} onChange={(e) => set('port', e.target.value)} placeholder={String(preset.port || 587)} className={inputCls} data-testid="email-port" /></div>
            <div><label className={LABEL}>Security</label><select value={v.security || preset.security} onChange={(e) => set('security', e.target.value)} className={inputCls} data-testid="email-security"><option value="starttls">STARTTLS</option><option value="ssl">SSL</option></select></div>
          </div>
        </>}
      </div>
      <p className="text-[11px] text-slate-500" data-testid="email-provider-hint">{HINTS[v.provider]}</p>
      {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2.5 py-1.5" data-testid="email-error">{err}</div>}
      {ok && <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5" data-testid="email-ok">{ok}</div>}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={save} disabled={!!busy || !v.username} className="inline-flex items-center gap-1.5 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white text-sm font-semibold px-4 py-2 rounded-lg" data-testid="email-save-btn">
          {busy === 'save' ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Check & connect
        </button>
        {data.connected && <>
          <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Send a test to…" className={`${inputCls} w-52 py-1.5 text-sm`} data-testid="email-test-to" />
          <button type="button" onClick={test} disabled={!!busy} className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-teal-500 text-sm font-semibold px-3 py-2 rounded-lg" data-testid="email-test-btn">
            {busy === 'test' ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test
          </button>
        </>}
        {data.source === 'workshop' && <button type="button" onClick={disconnect} disabled={!!busy} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-red-700" data-testid="email-disconnect-btn"><Unplug size={13} /> Disconnect</button>}
      </div>
    </div>
  );
};

export const EmailSettings = () => {
  const [data, setData] = useEmailSettings();
  if (!data) return <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  return (
    <div className="space-y-4">
      <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="email-account-section">
        <div>
          <h2 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">Your email account</h2>
          <p className="text-xs text-slate-500 mt-1">Every email to your customers and team goes out from this address. Works with Gmail, Outlook, Zoho or any email provider.</p>
        </div>
        <Status data={data} />
        <AccountForm key={data.account.verified_at || 'new'} data={data} onSaved={setData} />
      </section>
      <DailyEmailsCard data={data} onSaved={setData} />
    </div>
  );
};
